import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Loader2, CheckCircle, ExternalLink, MessageSquare, MessageCircle, Plus, Search, Menu, History, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { apiClient } from '../api/client';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useSidebar } from '@/hooks/useSidebar';
import { MobileMenuButton } from '@/components/MobileMenuButton';
import { webSocketClient, sendWorkflowRequestMessage, handleStreamingResponse } from '@/api/websocket';
import { ModeToggle } from '@/components/mode-toggle';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { IntegrationIcon, hasIntegrationIcon } from '@/components/IntegrationIcon';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  questions?: string[];
  status?: string;
  executionId?: string;
  workflowId?: string;
  workflowName?: string;
  requestId?: string;
}

interface WorkflowRequestEntry {
  id: string;
  prompt: string;
  status: string;
  category?: string;
  created_at: string;
  conversation_history?: any[];
  parameters?: Record<string, any>;
}

interface CatalogWorkflow {
  id: string;
  name: string;
  description?: string;
  categories?: string[];
  keywords?: string[];
  integrations?: string[];
}

// Map a request-catalog category to the integration id used by IntegrationIcon.
// Categories mostly match integration ids; the exceptions are k8s and cloud.
const CATALOG_CATEGORY_TO_INTEGRATION: Record<string, string> = {
  k8s: 'kubernetes',
  cloud: 'aws',
};

const catalogCategoryToIntegration = (cat: string): string =>
  CATALOG_CATEGORY_TO_INTEGRATION[cat] || cat;

// Human-friendly labels for filter chips.
const CATALOG_CATEGORY_LABELS: Record<string, string> = {
  k8s: 'Kubernetes',
  cloud: 'AWS',
  general: 'General',
  'argo-rollouts': 'Argo Rollouts',
  argocd: 'Argo CD',
  fluxcd: 'Flux CD',
  aws: 'AWS',
  oci: 'OCI',
  gitlab: 'GitLab',
  github: 'GitHub',
  circleci: 'CircleCI',
  sonarcloud: 'SonarCloud',
  okta: 'Okta',
  databricks: 'Databricks',
  gcp: 'Google Cloud',
  planetscale: 'PlanetScale',
  clickhouse: 'ClickHouse',
  pagerduty: 'PagerDuty',
  posthog: 'PostHog',
  flyio: 'Fly.io',
  vault: 'HashiCorp Vault',
  infisical: 'Infisical',
};

const catalogCategoryLabel = (cat: string): string =>
  CATALOG_CATEGORY_LABELS[cat] || cat.charAt(0).toUpperCase() + cat.slice(1);

// Normalized filter keys for a catalog workflow: trigger request categories
// (mapped to integration ids so "k8s" and a kubernetes block merge into one
// chip) plus the workflow's block integrations that have a brand icon. Used
// for both the filter chips and filtering, so every workflow that shows an
// icon is also reachable through a chip.
const catalogWorkflowFilterKeys = (wf: CatalogWorkflow): string[] => {
  const keys = new Set<string>();
  for (const cat of wf.categories || []) keys.add(catalogCategoryToIntegration(cat));
  for (const integration of wf.integrations || []) {
    if (hasIntegrationIcon(integration)) keys.add(integration);
  }
  if (keys.size === 0) keys.add('general');
  return Array.from(keys);
};

// Pick the icon to show next to a catalog workflow: prefer the trigger's request
// category, then fall back to the workflow's block integrations. The request
// trigger always maps to "slack" in the integrations list, so prefer any other
// integration first — it says more about what the workflow actually does.
const catalogWorkflowIcon = (wf: CatalogWorkflow): { integration: string; label: string } | null => {
  for (const cat of wf.categories || []) {
    const integration = catalogCategoryToIntegration(cat);
    if (hasIntegrationIcon(integration)) return { integration, label: catalogCategoryLabel(cat) };
  }
  const integrations = wf.integrations || [];
  const nonSlack = integrations.filter((i) => i !== 'slack');
  for (const integration of [...nonSlack, ...integrations]) {
    if (hasIntegrationIcon(integration)) return { integration, label: catalogCategoryLabel(integration) };
  }
  return null;
};

const SAMPLE_PROMPTS = [
  'Create a new ConfigMap in the production cluster',
  'Scale my deployment to 5 replicas',
  'Add a DNS record in Cloudflare',
  'Create a PagerDuty escalation policy',
];

// Four developer-request examples per integration. Used to build the empty-state
// suggestions from what the customer has actually connected.
const INTEGRATION_SAMPLE_PROMPTS: Record<string, string[]> = {
  kubernetes: [
    'Create a new ConfigMap in the production cluster',
    'Scale my deployment to 5 replicas',
    'Create a namespace for the new payments service',
    'Restart the checkout deployment in staging',
  ],
  aws: [
    'Create an S3 bucket for our ML training data',
    'Provision an RDS Postgres instance for staging',
    'Create an SQS queue for the order events',
    'Add an IAM role for the analytics service',
  ],
  oci: [
    'Provision a new OCI compute instance for the batch jobs',
    'Create an OCI object storage bucket for backups',
    'Set up an OCI autonomous database for staging',
    'Add a new OCI VCN subnet for the data team',
  ],
  cloudflare: [
    'Add a DNS record in Cloudflare',
    'Purge the Cloudflare cache for our marketing site',
    'Block an IP address in Cloudflare',
    'Create a Cloudflare page rule to redirect the old docs URL',
  ],
  pagerduty: [
    'Create a PagerDuty escalation policy',
    'Add a new PagerDuty service for the payments API',
    'Update the on-call schedule for the platform team',
    'Create a PagerDuty maintenance window for tonight',
  ],
  datadog: [
    'Create a Datadog monitor for API error rate',
    'Add a Datadog dashboard for the checkout service',
    'Mute Datadog alerts for the staging cluster this weekend',
    'Create a Datadog SLO for the payments API',
  ],
  argocd: [
    'Sync the payments application in ArgoCD',
    'Deploy the new service with ArgoCD',
    'Roll back the checkout app to the previous version in ArgoCD',
    'Create an ArgoCD application for the new microservice',
  ],
  'argo-rollouts': [
    'Promote the canary rollout for the API service',
    'Abort the current rollout for checkout',
    'Restart the rollout pods for the payments service',
    'Pause the canary rollout at 50% traffic',
  ],
  fluxcd: [
    'Reconcile the Kustomization for the platform namespace',
    'Suspend the HelmRelease for the legacy service',
    'Force a Flux sync for the apps repository',
    'Resume the suspended HelmRelease for checkout',
  ],
  karpenter: [
    'Raise the CPU limit on the default NodePool to 200 cores',
    'Set the gpu NodePool consolidation policy to WhenEmpty',
    'Recycle the node backing NodeClaim default-abc12',
    'Show the status of the spot NodePool',
  ],
  kyverno: [
    'List all Kyverno policy violations in production',
    'Switch the require-labels policy to Enforce',
    'Apply a policy that disallows the latest image tag',
    'Show the compliance summary for the payments namespace',
  ],
  trivy: [
    'List all critical CVEs in production',
    'Show the vulnerability report for the checkout deployment',
    'List exposed secrets found in container images',
    'Rescan the payments-api workload for vulnerabilities',
  ],
  github: [
    'Trigger the deploy GitHub Action on main',
    'Create a PR to bump the API image tag',
    'Create a new repository for the notifications service',
    'Add a branch protection rule to the payments repo',
  ],
  gitlab: [
    'Trigger the deploy pipeline in GitLab',
    'Create a merge request to update the config',
    'Create a new GitLab project for the data team',
    'Retry the failed pipeline on the main branch',
  ],
  helm: [
    'Upgrade the Redis Helm release to the latest chart',
    'Install the Prometheus Helm chart in the monitoring namespace',
    'Roll back the ingress-nginx Helm release',
    'List the values for the Kafka Helm release',
  ],
  vercel: [
    'Roll back the production deployment on Vercel',
    'Promote the latest preview deployment to production',
    'Add an environment variable to the marketing site on Vercel',
    'Redeploy the docs project on Vercel',
  ],
  railway: [
    'Restart the API service on Railway',
    'Roll back the last deployment on Railway',
    'Set an environment variable for the worker service on Railway',
    'Redeploy the backend service on Railway',
  ],
  flyio: [
    'Restart the crashed machine for the API app on Fly.io',
    'Scale the web app to 3 machines on Fly.io',
    'Set a secret for the backend app on Fly.io',
    'Stop the staging machines on Fly.io for the weekend',
  ],
  nebius: [
    'Restart the GPU instance in the training project on Nebius',
    'Scale the Nebius node group to 5 nodes',
    'Stop the idle GPU instances in the dev project on Nebius',
    'Start the inference instance on Nebius',
  ],
  beam: [
    'Scale the inference deployment to 3 containers on Beam',
    'Stop the dev deployment on Beam',
    'Restart the embedding service deployment on Beam',
    'Start the batch processing deployment on Beam',
  ],
  daytona: [
    'Create a new Daytona sandbox for the onboarding project',
    'Restart my stopped Daytona sandbox',
    'Archive the unused Daytona sandboxes from last sprint',
    'Set an auto-stop interval for the dev sandboxes on Daytona',
  ],
  supabase: [
    'Create a Supabase branch for the new feature',
    'Merge the preview branch into production on Supabase',
    'Create a restore point for the production Supabase project',
    'Update the network restrictions on the Supabase project',
  ],
  planetscale: [
    'Create a PlanetScale branch for the schema change',
    'Deploy the open deploy request for the orders database',
    'Revert the last deploy request on PlanetScale',
    'Create a deploy request for my feature branch on PlanetScale',
  ],
  neon: [
    'Create a Neon branch for the preview environment',
    'Get the connection string for the staging branch on Neon',
    'Reset my Neon dev branch from its parent',
    'Suspend the idle compute on the dev Neon project',
  ],
  clickhouse: [
    'Start the stopped ClickHouse service',
    'Update the autoscaling settings for the analytics ClickHouse service',
    'Restore the latest backup for the events ClickHouse service',
    'Resync the failed ClickPipe for the events stream',
  ],
  jenkins: [
    'Trigger the deploy job in Jenkins',
    'Retry the failed nightly build in Jenkins',
    'Get the console log for the last release build in Jenkins',
    'Stop the stuck build in Jenkins',
  ],
  circleci: [
    'Rerun the failed workflow on main in CircleCI',
    'Trigger the deploy pipeline in CircleCI',
    'Approve the pending hold job in CircleCI',
    'Cancel the running workflow for my branch in CircleCI',
  ],
  sonarcloud: [
    'Check the quality gate status for my project in SonarCloud',
    'List the open blocker vulnerabilities in SonarCloud',
    'Show the security hotspots awaiting review in SonarCloud',
    'Mark this SonarCloud issue as a false positive',
    'Assign the new critical vulnerabilities to our security champion in SonarCloud',
    'Get the coverage and duplication metrics for main in SonarCloud',
  ],
  okta: [
    'Look up the Okta profile for a locked-out user',
    'List the users in the Engineering group in Okta',
    'Query the Okta system log for failed sign-in attempts',
    'Clear all active sessions for a compromised user in Okta',
    'Suspend a deactivated contractor account in Okta',
    'Add the new hire to the VPN access group in Okta',
  ],
  gcp: [
    'Roll back the checkout Cloud Run service to its last working revision',
    'Restart the orders Cloud SQL instance',
    'Show me which GCP services drove last week\'s spend increase',
    'Find and clean up unattached persistent disks in the prod project',
    'Remove public access from the customer-exports bucket',
    'Retry the failed Cloud Build for the main branch',
  ],
  databricks: [
    'Re-run the failed Databricks job and post the error to Slack',
    'List the Databricks jobs that failed in the last 24 hours',
    'Terminate the idle all-purpose Databricks cluster',
    'Restart the stuck Databricks pipeline update',
    'Start the SQL warehouse for the analytics team in Databricks',
  ],
  terraform: [
    'Apply the pending Terraform run for the networking workspace',
    'Queue a Terraform plan for the production workspace',
    'Discard the stale Terraform run in the staging workspace',
    'Unlock the Terraform workspace for the platform team',
  ],
  pulumi: [
    'Run a Pulumi preview for the infra stack',
    'Deploy the staging stack with Pulumi',
    'Get the stack outputs for the production Pulumi stack',
    'Pause deployments on the dev Pulumi stack',
  ],
  vault: [
    'Create a new KV secret for the payments service in Vault',
    'List the secrets under the app mount in Vault',
    'Rotate the database credentials stored in Vault',
    'Check the seal status of the Vault cluster',
  ],
  infisical: [
    'Create a secret in the production environment in Infisical',
    'List the secrets in the backend project on Infisical',
    'Trigger the AWS secret sync for the platform project in Infisical',
    'Show the pending secret approval requests in Infisical',
  ],
};

// Pick 4 example prompts based on connected integrations: with >=4 integrations,
// one example each; with fewer, round-robin so some integrations contribute more.
// Integrations are shuffled first — the server returns them in a fixed order
// (kubernetes, github, gitlab, ...), so without a shuffle, accounts with many
// integrations would always see the same 4 examples.
function buildSamplePrompts(connectedIds: string[]): string[] {
  const withExamples = connectedIds.filter((id) => INTEGRATION_SAMPLE_PROMPTS[id]);
  if (withExamples.length === 0) return SAMPLE_PROMPTS;

  for (let i = withExamples.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [withExamples[i], withExamples[j]] = [withExamples[j], withExamples[i]];
  }

  const prompts: string[] = [];
  if (withExamples.length >= 4) {
    for (const id of withExamples.slice(0, 4)) {
      prompts.push(INTEGRATION_SAMPLE_PROMPTS[id][0]);
    }
  } else {
    let exampleIdx = 0;
    while (prompts.length < 4 && exampleIdx < 4) {
      for (const id of withExamples) {
        if (prompts.length >= 4) break;
        const example = INTEGRATION_SAMPLE_PROMPTS[id][exampleIdx];
        if (example) prompts.push(example);
      }
      exampleIdx++;
    }
  }
  return prompts.length > 0 ? prompts : SAMPLE_PROMPTS;
}

export default function WorkflowRequestChat() {
  const { isMobile } = useSidebar();

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [requestId, setRequestId] = useState<string | null>(null);
  const requestIdRef = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [history, setHistory] = useState<WorkflowRequestEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [samplePrompts, setSamplePrompts] = useState<string[]>(SAMPLE_PROMPTS);
  const [samplePromptsLoading, setSamplePromptsLoading] = useState(true);
  const [showCatalog, setShowCatalog] = useState(false);
  const [catalog, setCatalog] = useState<CatalogWorkflow[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogCategoryFilter, setCatalogCategoryFilter] = useState<string | null>(null);
  const [catalogPanelWidth, setCatalogPanelWidth] = useState(() => Math.round(window.innerWidth * 0.28));
  const isResizingCatalog = useRef(false);
  const resizeCatalogLineRef = useRef<HTMLDivElement>(null);

  const startCatalogResize = useCallback(() => {
    isResizingCatalog.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    if (resizeCatalogLineRef.current) {
      resizeCatalogLineRef.current.style.transitionDelay = '0ms';
      resizeCatalogLineRef.current.style.backgroundColor = 'hsl(var(--primary) / 0.5)';
    }
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (isResizingCatalog.current) {
        const newWidth = Math.max(Math.round(window.innerWidth * 0.23), Math.min(Math.round(window.innerWidth * 0.35), window.innerWidth - e.clientX));
        setCatalogPanelWidth(newWidth);
      }
    };
    const onMouseUp = () => {
      if (isResizingCatalog.current) {
        isResizingCatalog.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        if (resizeCatalogLineRef.current) {
          resizeCatalogLineRef.current.style.transitionDelay = '0ms';
          resizeCatalogLineRef.current.style.backgroundColor = 'transparent';
        }
      }
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  // Lazy-load the catalog of developer-request workflows this user can trigger
  // the first time the panel is opened.
  useEffect(() => {
    // NOTE: catalogLoading must not be a dependency here — setting it inside
    // the effect would re-trigger the effect and cancel the in-flight fetch.
    if (!showCatalog || catalogLoaded) return;
    let cancelled = false;
    setCatalogLoading(true);
    apiClient.getWorkflowRequestCatalog()
      .then((data) => {
        if (cancelled) return;
        setCatalog(data.workflows || []);
        setCatalogLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setCatalogLoaded(true);
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => { cancelled = true; };
  }, [showCatalog, catalogLoaded]);

  // Tailor the empty-state examples to what the customer actually has connected
  useEffect(() => {
    let cancelled = false;
    apiClient.getWorkflowIntegrationsStatus()
      .then((statuses) => {
        if (cancelled) return;
        const connectedIds = statuses.filter((s) => s.connected).map((s) => s.id);
        setSamplePrompts(buildSamplePrompts(connectedIds));
      })
      .catch(() => {
        // Keep the generic defaults if the status call fails
      })
      .finally(() => {
        if (!cancelled) setSamplePromptsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Keep a ref of the active request id so the once-registered push handler
  // always sees the latest value without re-subscribing.
  useEffect(() => {
    requestIdRef.current = requestId;
  }, [requestId]);

  // WebSocket connection status
  useEffect(() => {
    const connectHandler = () => setIsConnected(true);
    const disconnectHandler = () => setIsConnected(false);
    webSocketClient.onConnect(connectHandler);
    webSocketClient.onDisconnect(disconnectHandler);
    // Connect the WebSocket if not already connected
    if (webSocketClient.isConnected()) {
      setIsConnected(true);
    } else {
      webSocketClient.connect().then(() => setIsConnected(true)).catch(() => setIsConnected(false));
    }
    return () => {
      // cleanup if needed
    };
  }, []);

  // Listen for server-pushed messages (e.g., justification prompts via internal push)
  useEffect(() => {
    const pushHandler = (msg: any) => {
      if (msg.type === 'chat_response' && msg.content?.done === true && msg.id?.startsWith('push-')) {
        const text = msg.content?.textChunk;
        const rid = msg.content?.request_id;
        // Only render pushes that belong to THIS chat session's active request.
        // A stale push (e.g. an approval resolved for an older session's request)
        // is broadcast to all of the user's connections; drop it here.
        if (!text || !rid) return;
        if (requestIdRef.current === null || rid !== requestIdRef.current) return;
        setMessages(prev => {
          // Only dedup if an identical message was added in the last 5 seconds
          const now = Date.now();
          const recentDuplicate = prev.some(m => m.content === text && (now - m.timestamp.getTime()) < 5000);
          if (recentDuplicate) return prev;
          return [...prev, { id: `push-${now}`, role: 'assistant', content: text, timestamp: new Date() }];
        });
      }
    };
    webSocketClient.onMessage(pushHandler);
    return () => { webSocketClient.offMessage(pushHandler); };
  }, []);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const data = await apiClient.getWorkflowRequests(1, 50, true);
      const requests = data?.requests || [];
      setHistory(requests);
      setIsConnected(true);
    } catch (err) {
      setIsConnected(false);
      console.error('Failed to fetch workflow request history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadRequest = (entry: WorkflowRequestEntry) => {
    setRequestId(entry.id);
    const loaded: Message[] = [];

    // Initial user prompt
    loaded.push({
      id: `user-${entry.id}`,
      role: 'user',
      content: entry.prompt,
      timestamp: new Date(entry.created_at),
    });

    // Reconstruct from conversation_history
    // Supports both formats: [{role, content}] (new) and [{user_response}] (legacy)
    if (entry.conversation_history && entry.conversation_history.length > 0) {
      entry.conversation_history.forEach((item: any, idx: number) => {
        if (item.role === 'user') {
          // Skip the first user message if it matches the initial prompt (avoid duplicate)
          if (idx === 0 && item.content === entry.prompt) return;
          // Deduplicate: skip if the last loaded message has same role+content
          const last = loaded[loaded.length - 1];
          if (last && last.role === 'user' && last.content === (item.content || '')) return;
          loaded.push({
            id: `hist-${idx}`,
            role: 'user',
            content: item.content || '',
            timestamp: new Date(entry.created_at),
          });
        } else if (item.role === 'assistant') {
          // Deduplicate: skip if the last loaded message has same role+content
          const last = loaded[loaded.length - 1];
          if (last && last.role === 'assistant' && last.content === (item.content || '')) return;
          loaded.push({
            id: `hist-${idx}`,
            role: 'assistant',
            content: item.content || '',
            timestamp: new Date(entry.created_at),
            status: item.status,
          });
        } else if (item.user_response) {
          // Legacy format
          loaded.push({
            id: `hist-${idx}`,
            role: 'user',
            content: item.user_response,
            timestamp: new Date(entry.created_at),
          });
        }
      });
    }

    // Add status summary only if no assistant message exists yet (backward compat)
    const hasAssistantMsg = loaded.some(m => m.role === 'assistant');
    if (!hasAssistantMsg) {
      loaded.push({
        id: `status-${entry.id}`,
        role: 'assistant',
        content: getStatusMessage(entry.status),
        timestamp: new Date(entry.created_at),
        status: entry.status,
      });
    }

    // If still awaiting the requester's confirmation, mark the last assistant
    // message so the Yes/No buttons render again on reopen.
    if (entry.status === 'pending_confirmation') {
      for (let i = loaded.length - 1; i >= 0; i--) {
        if (loaded[i].role === 'assistant') {
          loaded[i] = { ...loaded[i], status: 'pending_confirmation', requestId: entry.id };
          break;
        }
      }
    }

    setMessages(loaded);
    if (isMobile) setShowHistory(false);
  };

  const getStatusMessage = (status: string): string => {
    switch (status) {
      case 'completed': return 'Your request was completed successfully.';
      case 'executing': return 'Your request is currently executing.';
      case 'matched': return 'Your request was matched to a workflow and is being processed.';
      case 'awaiting_params': return 'This request is waiting for more details from you. You can continue the conversation below.';
      case 'awaiting_justification': return 'This request is waiting for your justification. Please provide it below.';
      case 'failed': return 'This request encountered an error.';
      case 'no_workflow': return 'No matching workflow was found for this request. Your platform team has been notified.';
      case 'pending_confirmation': return 'No matching workflow found for your request. Would you like to send it to your platform team as a workflow request?';
      case 'dismissed': return 'This request was not sent to your platform team.';
      default: return 'Your request is being processed.';
    }
  };

  const pollForJustification = useCallback((rid: string) => {
    let attempts = 0;
    const maxAttempts = 15;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const result = await apiClient.getPendingJustification(rid);
        if (result.pending && result.prompt_message) {
          clearInterval(interval);
          // Only add if not already displayed (e.g., via WebSocket push)
          setMessages(prev => {
            if (prev.some(m => m.content?.includes(result.prompt_message!))) return prev;
            return [...prev, {
              id: `justification-${Date.now()}`,
              role: 'assistant',
              content: result.prompt_message!,
              timestamp: new Date(),
            }];
          });
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
        }
      } catch {
        if (attempts >= maxAttempts) {
          clearInterval(interval);
        }
      }
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const handleSubmit = (text?: string) => {
    const messageText = text || input.trim();
    if (!messageText || isLoading || !isConnected) return;

    // Prevent duplicate messages (debounce double-clicks/double-enters)
    const lastMsg = messages[messages.length - 1];
    if (lastMsg && lastMsg.role === 'user' && lastMsg.content === messageText &&
        Date.now() - lastMsg.timestamp.getTime() < 2000) {
      return;
    }

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: messageText,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    const messageId = sendWorkflowRequestMessage(messageText, requestId || undefined);

    const handler = handleStreamingResponse(
      messageId,
      (chunk) => {
        setMessages(prev => {
          const last = prev[prev.length - 1];
          if (last && last.role === 'assistant' && last.id === `assistant-${messageId}`) {
            return [...prev.slice(0, -1), { ...last, content: last.content + chunk }];
          }
          return [...prev, { id: `assistant-${messageId}`, role: 'assistant', content: chunk, timestamp: new Date() }];
        });
      },
      () => {
        setIsLoading(false);
        fetchHistory();
      },
      () => {
        setMessages(prev => {
          if (prev.some(m => m.id === `thinking-${messageId}`)) return prev;
          return [...prev, { id: `thinking-${messageId}`, role: 'assistant', content: 'Routing your request...', timestamp: new Date() }];
        });
      },
      () => {
        setMessages(prev => prev.filter(m => m.id !== `thinking-${messageId}`));
      },
      () => { setIsLoading(false); },
      undefined,
      (error) => {
        setMessages(prev => prev.filter(m => m.id !== `thinking-${messageId}`));
        setMessages(prev => [...prev, { id: `error-${Date.now()}`, role: 'assistant', content: error, timestamp: new Date() }]);
        setIsLoading(false);
      },
    );

    const handleResponse = (msg: any) => {
      handler(msg);
      if (msg.id === messageId && msg.content?.type === 'answer' && msg.content?.done === true) {
        const rid = msg.content?.request_id;
        const status = msg.content?.status;
        // Only keep requestId if clarification is needed (awaiting_params)
        // For terminal statuses, reset so the next message starts a new request
        // For conversational answers during an active request, KEEP the requestId
        if ((status === 'awaiting_params' || status === 'awaiting_justification') && rid) {
          setRequestId(rid);
        } else if (status === 'pending_confirmation' && rid) {
          // Keep the requestId so a typed "yes"/"no" reply resolves the confirmation
          setRequestId(rid);
        } else if (status === 'conversation') {
          // Don't reset — user might be asking questions about parameters mid-flow
        } else if (status === 'executing' || status === 'matched') {
          // Keep the requestId temporarily and poll for pending justification
          if (rid) {
            setRequestId(rid);
            pollForJustification(rid);
          } else {
            setRequestId(null);
          }
        } else if (status === 'no_workflow' || status === 'dismissed' || status === 'access_denied') {
          setRequestId(null);
        }
        if (status) {
          setMessages(prev => {
            const last = prev[prev.length - 1];
            if (last && last.id === `assistant-${messageId}`) {
              return [...prev.slice(0, -1), { ...last, status, workflowName: msg.content?.workflow_name, requestId: rid }];
            }
            return prev;
          });
        }
        webSocketClient.offMessage(handleResponse);
      }
      if (msg.type === 'error' && msg.id === messageId) {
        webSocketClient.offMessage(handleResponse);
      }
    };

    webSocketClient.onMessage(handleResponse);
  };

  const handleNewConversation = () => {
    setMessages([]);
    setRequestId(null);
    setInput('');
    fetchHistory();
    if (isMobile) setShowHistory(false);
    inputRef.current?.focus();
  };

  const [confirmingRequest, setConfirmingRequest] = useState(false);

  const handleConfirmRequest = async (rid: string, confirm: boolean) => {
    if (confirmingRequest) return;
    setConfirmingRequest(true);
    try {
      const result = await apiClient.confirmWorkflowRequest(rid, confirm);
      setMessages(prev => [
        // Mark prior pending_confirmation messages as resolved so buttons disappear
        ...prev.map(m => (m.status === 'pending_confirmation' ? { ...m, status: result.status } : m)),
        {
          id: `confirm-${Date.now()}`,
          role: 'assistant' as const,
          content: result.summary,
          timestamp: new Date(),
          status: result.status,
        },
      ]);
      setRequestId(null);
      fetchHistory();
    } catch (err: any) {
      setMessages(prev => [...prev, {
        id: `confirm-err-${Date.now()}`,
        role: 'assistant' as const,
        content: err?.response?.data?.error || 'Failed to update the request. Please try again.',
        timestamp: new Date(),
      }]);
    } finally {
      setConfirmingRequest(false);
    }
  };

  const isConversationComplete = messages.some(
    m => m.status === 'matched' || m.status === 'executing' || m.status === 'completed' || m.status === 'no_workflow' || m.status === 'failed'
  );

  const filteredHistory = history.filter(entry =>
    entry.prompt.toLowerCase().includes(historySearch.toLowerCase())
  );

  // Distinct categories across the catalog, ordered by frequency so the most
  // common integrations surface first in the filter row.
  const catalogCategories = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const wf of catalog) {
      for (const key of catalogWorkflowFilterKeys(wf)) {
        counts.set(key, (counts.get(key) || 0) + 1);
      }
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([key]) => key);
  }, [catalog]);

  const catalogSearchLower = catalogSearch.trim().toLowerCase();
  const filteredCatalog = catalog.filter((wf) => {
    if (catalogCategoryFilter && !catalogWorkflowFilterKeys(wf).includes(catalogCategoryFilter)) return false;
    if (!catalogSearchLower) return true;
    return (
      wf.name.toLowerCase().includes(catalogSearchLower) ||
      (wf.description || '').toLowerCase().includes(catalogSearchLower) ||
      catalogWorkflowFilterKeys(wf).some((k) => k.toLowerCase().includes(catalogSearchLower) || catalogCategoryLabel(k).toLowerCase().includes(catalogSearchLower)) ||
      (wf.categories || []).some((c) => c.toLowerCase().includes(catalogSearchLower) || catalogCategoryLabel(c).toLowerCase().includes(catalogSearchLower)) ||
      (wf.keywords || []).some((k) => k.toLowerCase().includes(catalogSearchLower))
    );
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const HistorySidebar = ({ isDrawer = false }: { isDrawer?: boolean }) => (
    <div className={cn(
      "bg-background border-r border-border flex flex-col h-full",
      isDrawer ? "w-full" : "w-80"
    )}>
      <div className="p-3 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-foreground">Request History</h2>
          <Button onClick={handleNewConversation} size="sm" className="gap-2">
            <Plus className="h-4 w-4" />
            New Request
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search requests..."
            value={historySearch}
            onChange={(e) => setHistorySearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {historyLoading ? (
          <div className="p-4 text-center text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
            Loading history...
          </div>
        ) : filteredHistory.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p className="text-sm">
              {historySearch ? 'No requests found' : 'No request history yet'}
            </p>
            {!historySearch && (
              <p className="text-xs mt-1">
                Submit a request to see your history
              </p>
            )}
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {filteredHistory.map((entry) => (
              <button
                key={entry.id}
                onClick={() => loadRequest(entry)}
                className={cn(
                  "w-full text-left px-3 py-2.5 rounded-lg transition-all duration-200 group",
                  requestId === entry.id
                    ? "bg-primary/10 border border-primary/30"
                    : "hover:bg-muted/50 border border-transparent"
                )}
              >
                <div className="flex items-start gap-2">
                  <MessageSquare className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {entry.prompt}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground">
                        {new Date(entry.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(entry.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const CatalogSidebar = ({ isDrawer = false }: { isDrawer?: boolean }) => (
    <div
      className={cn(
        "bg-background flex flex-col h-full",
        isDrawer ? "w-full" : "min-w-0 border-l border-border overflow-hidden"
      )}
      style={isDrawer ? undefined : { width: catalogPanelWidth }}
    >
      <div className="p-3 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-sm font-mono uppercase tracking-wider text-muted-foreground truncate">Developer Request workflows</h2>
            {!catalogLoading && catalogLoaded && (
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground flex-shrink-0">
                {filteredCatalog.length === catalog.length ? catalog.length : `${filteredCatalog.length}/${catalog.length}`}
              </span>
            )}
          </div>
          {!isDrawer && (
            <button
              onClick={() => setShowCatalog(false)}
              className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
              aria-label="Close catalog"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, integration, keyword..."
            value={catalogSearch}
            onChange={(e) => setCatalogSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        {catalogCategories.length > 1 && (
          <div className="flex items-center gap-1.5 flex-wrap mt-2.5">
            <button
              onClick={() => setCatalogCategoryFilter(null)}
              className={cn(
                'text-[11px] font-mono px-2 py-1 rounded-md border transition-colors',
                catalogCategoryFilter === null
                  ? 'bg-primary/10 text-primary border-primary/30'
                  : 'bg-transparent text-muted-foreground border-border/50 hover:border-border hover:text-foreground'
              )}
            >
              All
            </button>
            {catalogCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCatalogCategoryFilter(catalogCategoryFilter === cat ? null : cat)}
                className={cn(
                  'inline-flex items-center gap-1.5 text-[11px] font-mono px-2 py-1 rounded-md border transition-colors',
                  catalogCategoryFilter === cat
                    ? 'bg-primary/10 text-primary border-primary/30'
                    : 'bg-transparent text-muted-foreground border-border/50 hover:border-border hover:text-foreground'
                )}
                title={catalogCategoryLabel(cat)}
              >
                <IntegrationIcon integration={catalogCategoryToIntegration(cat)} size={12} />
                {catalogCategoryLabel(cat)}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {catalogLoading ? (
          <div className="p-3 space-y-2" aria-hidden="true">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={`catalog-skeleton-${i}`} className="px-3 py-2.5 rounded-md border border-border/50">
                <div className="h-3.5 w-2/5 rounded bg-muted animate-pulse" />
                <div className="h-3 w-4/5 rounded bg-muted animate-pulse mt-2" />
              </div>
            ))}
          </div>
        ) : catalog.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <p className="text-sm text-muted-foreground">No request workflows available to you yet.</p>
            <p className="text-xs text-muted-foreground mt-1">Ask your platform team to create workflows with a developer request trigger.</p>
          </div>
        ) : filteredCatalog.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <p className="text-sm text-muted-foreground">No workflows match your filters.</p>
            <button
              onClick={() => { setCatalogSearch(''); setCatalogCategoryFilter(null); }}
              className="text-xs text-primary hover:underline mt-1"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {filteredCatalog.map((wf) => {
              const icon = catalogWorkflowIcon(wf);
              return (
                <div
                  key={wf.id}
                  className="px-3 py-2.5 rounded-md border border-transparent hover:border-border/50 hover:bg-muted/40 transition-colors"
                >
                  <div className="flex items-start gap-2.5 min-w-0">
                    {icon && (
                      <span className="flex-shrink-0 mt-0.5" title={icon.label}>
                        <IntegrationIcon integration={icon.integration} size={16} />
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{wf.name}</p>
                      {wf.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{wf.description}</p>
                      )}
                      {(wf.keywords?.length ?? 0) > 0 && (
                        <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                          {wf.keywords?.map((k) => (
                            <span key={`kw-${k}`} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border/50">
                              {k}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-background">
      {/* History Sidebar */}
      {isMobile ? (
        <Sheet open={showHistory} onOpenChange={setShowHistory}>
          <SheetContent side="left" className="p-0 w-80">
            <HistorySidebar isDrawer />
          </SheetContent>
        </Sheet>
      ) : (
        showHistory && (
          <HistorySidebar />
        )
      )}

      {/* Main Chat Area */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <MobileMenuButton />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowHistory(!showHistory)}
                className="p-2 flex-shrink-0"
                title="Toggle request history"
                aria-label={showHistory ? "Hide request history" : "Show request history"}
              >
                {isMobile ? <History className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
              <div className="min-w-0">
                <h2 className="text-xl sm:text-2xl font-bold text-foreground">
                  Developer Request Agent
                </h2>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></span>
                  <span className={`text-xs ${isConnected ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{isConnected ? 'Connected' : 'Disconnected'}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-1 mr-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCatalog(!showCatalog)}
                className={cn('text-xs font-medium', showCatalog && 'bg-muted')}
                aria-expanded={showCatalog}
                aria-label={showCatalog ? 'Hide workflow catalog' : 'Show workflow catalog'}
              >
                Catalog
              </Button>
              <ModeToggle />
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-4 py-6">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center animate-in fade-in-50 duration-700">
              <h3 className="text-2xl font-semibold text-foreground mb-3">
                What would you like to request?
              </h3>
              <p className="text-muted-foreground text-base max-w-md mb-6 leading-relaxed">
                Request changes to your platform — infra, integrations, tooling, and more. Kestrel will route your request to the right workflow and make sure it contains all the necessary details.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 w-full max-w-lg">
                {samplePromptsLoading
                  ? Array.from({ length: 4 }).map((_, i) => (
                      <div
                        key={`prompt-skeleton-${i}`}
                        className="p-3.5 rounded-lg border border-border/50 bg-card/50"
                        aria-hidden="true"
                      >
                        <div className="h-4 my-0.5 w-4/5 rounded bg-muted animate-pulse" />
                      </div>
                    ))
                  : samplePrompts.map((prompt) => (
                      <button
                        key={prompt}
                        onClick={() => handleSubmit(prompt)}
                        className="text-left p-3.5 bg-card/50 backdrop-blur-sm border border-border/50 rounded-lg hover:bg-card/80 hover:border-border hover:shadow-md transition-all duration-300 text-sm leading-relaxed"
                      >
                        <div className="flex items-start gap-2">
                          <MessageCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                          <span className="text-sm">{prompt}</span>
                        </div>
                      </button>
                    ))}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={cn(
                    'flex',
                    message.role === 'user' ? 'justify-end' : 'justify-start'
                  )}
                >
                  {message.id.startsWith('thinking-') ? (
                    <div className="animate-in slide-in-from-bottom-2 duration-300 flex gap-3 justify-start">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-muted to-muted/50 border border-border/50 flex items-center justify-center">
                        <Send className="h-3 w-3 text-muted-foreground" />
                      </div>
                      <div className="rounded-2xl px-3 py-2">
                        <p className="text-sm font-medium shimmer-text">Thinking</p>
                      </div>
                    </div>
                  ) : (
                  <div
                    className={cn(
                      'max-w-[85%] rounded-2xl px-3 py-2 shadow-sm',
                      message.role === 'user'
                        ? 'bg-foreground text-background'
                        : 'bg-card/80 backdrop-blur-sm border border-border/50 text-foreground'
                    )}
                  >
                    <div className="text-sm prose-sm">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                          em: ({ children }) => <em className="italic">{children}</em>,
                          code: ({ className, children, ...props }) => {
                            const isBlock = className?.startsWith('language-');
                            if (isBlock) {
                              return (
                                <div className="my-2 rounded-md overflow-hidden border border-border/50">
                                  <div className="flex items-center justify-between px-3 py-1.5 bg-muted/50 border-b border-border/50">
                                    <span className="text-[10px] font-mono text-muted-foreground uppercase">{className?.replace('language-', '') || 'code'}</span>
                                    <button
                                      onClick={() => navigator.clipboard.writeText(String(children).replace(/\n$/, ''))}
                                      className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                      Copy
                                    </button>
                                  </div>
                                  <pre className="p-3 overflow-x-auto bg-muted/30">
                                    <code className="text-xs font-mono">{children}</code>
                                  </pre>
                                </div>
                              );
                            }
                            return (
                              <code className="px-1.5 py-0.5 rounded bg-muted text-xs font-mono" {...props}>
                                {children}
                              </code>
                            );
                          },
                          pre: ({ children }) => <>{children}</>,
                          ul: ({ children }) => <ul className="mb-2 ml-4 list-disc space-y-1">{children}</ul>,
                          ol: ({ children }) => <ol className="mb-2 ml-4 list-decimal space-y-1">{children}</ol>,
                          li: ({ children }) => <li>{children}</li>,
                          h1: ({ children }) => <h1 className="text-lg font-bold mb-2 mt-3">{children}</h1>,
                          h2: ({ children }) => <h2 className="text-base font-bold mb-2 mt-3">{children}</h2>,
                          h3: ({ children }) => <h3 className="text-sm font-bold mb-1 mt-2">{children}</h3>,
                          h4: ({ children }) => <h4 className="text-sm font-semibold mb-1 mt-2">{children}</h4>,
                          hr: () => <hr className="my-3 border-border/50" />,
                          a: ({ href, children }) => (
                            <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80">
                              {children}
                            </a>
                          ),
                          blockquote: ({ children }) => (
                            <blockquote className="border-l-2 border-primary/30 pl-3 my-2 text-muted-foreground italic">{children}</blockquote>
                          ),
                        }}
                      >
                        {message.content}
                      </ReactMarkdown>
                    </div>

                    {message.questions && message.questions.length > 0 && (
                      <ul className="mt-2 space-y-1.5">
                        {message.questions.map((q, i) => (
                          <li key={i} className="text-sm flex gap-2">
                            <span className="text-muted-foreground font-medium shrink-0">{i + 1}.</span>
                            <span>{q}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {(message.status === 'matched' || message.status === 'executing' || message.status === 'completed') && (
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-600 dark:text-green-400">
                          <CheckCircle className="h-3.5 w-3.5" />
                          {message.status === 'completed' ? 'Completed' : message.status === 'executing' ? 'Executing' : 'Matched'}
                        </span>
                        {message.executionId && message.workflowId && (
                          <a
                            href={`/workflows/${message.workflowId}/executions/${message.executionId}`}
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                          >
                            View Execution <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    )}

                    {message.status === 'pending_confirmation' && (message.requestId || requestId) && (
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <Button
                          size="sm"
                          disabled={confirmingRequest}
                          onClick={() => handleConfirmRequest(message.requestId || requestId!, true)}
                        >
                          Yes, send to platform team
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={confirmingRequest}
                          onClick={() => handleConfirmRequest(message.requestId || requestId!, false)}
                        >
                          No, dismiss
                        </Button>
                      </div>
                    )}
                  </div>
                  )}
                </div>
              ))}

              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area — styled to match the Home / Workflow Assistant chat input */}
        <div className="sticky bottom-0 bg-background px-2 pt-2 pb-5">
          <div className="max-w-4xl mx-auto">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
              className="relative flex items-end gap-2 p-2 border border-border rounded-xl bg-background"
            >
              <textarea
                ref={inputRef as any}
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 96) + 'px';
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                placeholder={requestId ? 'Reply with more details...' : 'What do you need?'}
                disabled={isLoading}
                rows={1}
                className="flex-1 min-h-[40px] max-h-24 py-2 px-3 bg-transparent border-0 text-foreground text-base leading-relaxed placeholder:text-muted-foreground/60 focus:outline-none disabled:opacity-50 resize-none overflow-y-auto"
              />
              <div className="flex-shrink-0 self-center">
                <Button
                  type="submit"
                  size="icon"
                  disabled={!input.trim() || isLoading}
                  className={cn(
                    "h-9 w-9 rounded-lg transition-all duration-200",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    "bg-foreground hover:bg-foreground/90 text-background",
                    !input.trim() || isLoading
                      ? "scale-95 opacity-50"
                      : "scale-100 hover:scale-105 shadow-lg"
                  )}
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-3 w-3" />
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Catalog Sidebar (right, hidden by default) */}
      {/* Rendered via function call (not JSX component) so the search input
          isn't remounted — and doesn't lose focus — on every keystroke. */}
      {isMobile ? (
        <Sheet open={showCatalog} onOpenChange={setShowCatalog}>
          <SheetContent side="right" className="p-0 w-80">
            {CatalogSidebar({ isDrawer: true })}
          </SheetContent>
        </Sheet>
      ) : (
        showCatalog && (
          <>
            <div
              onMouseDown={(e) => { e.preventDefault(); startCatalogResize(); }}
              onMouseEnter={() => {
                if (!isResizingCatalog.current && resizeCatalogLineRef.current) {
                  resizeCatalogLineRef.current.style.transitionDelay = '300ms';
                  resizeCatalogLineRef.current.style.backgroundColor = 'hsl(var(--border))';
                }
              }}
              onMouseLeave={() => {
                if (!isResizingCatalog.current && resizeCatalogLineRef.current) {
                  resizeCatalogLineRef.current.style.transitionDelay = '0ms';
                  resizeCatalogLineRef.current.style.backgroundColor = 'transparent';
                }
              }}
              className="shrink-0 cursor-col-resize z-20 flex items-stretch justify-center"
              style={{ width: '9px', marginLeft: '-4px', marginRight: '-5px' }}
            >
              <div
                ref={resizeCatalogLineRef}
                className="w-[3px] rounded-full bg-transparent transition-colors"
                style={{ transitionDuration: '150ms', transitionDelay: '0ms', pointerEvents: 'none' }}
              />
            </div>
            {CatalogSidebar({})}
          </>
        )
      )}
    </div>
  );
}
